/** @typedef {import('../types/anomalyTimeSeries.types').AnomalySeverity} AnomalySeverity */

import { formatAnomalyDisplayDate, formatAnomalyHeatmapPeriod } from "../../anomalyDateUtils";

const isMonthGranularityPeriod = (value) => {
  const str = String(value);
  return /^\d{4}-\d{2}$/.test(str) || /^\d{4}-\d{2}-01$/.test(str);
};

/** Timeline period label — month periods use heatmap style (MMM YYYY), daily use DD-MM-YYYY. */
export const formatTimelinePeriod = (value) => {
  if (value == null || value === "") return "—";
  if (isMonthGranularityPeriod(value)) return formatAnomalyHeatmapPeriod(value);
  return formatAnomalyDisplayDate(value);
};

export { formatAnomalyDisplayPeriodShort } from "../../anomalyDateUtils";

export const formatValue = (value) => {
  if (value == null || Number.isNaN(value)) return "—";
  return typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 4 }) : String(value);
};

/**
 * @param {number|null|undefined} pct
 * @param {(key: string, opts?: object) => string} t
 */
export const formatDeviation = (pct, t) => {
  if (pct == null || Number.isNaN(pct)) return "—";
  const abs = Math.abs(pct).toFixed(1);
  if (pct > 0) {
    return t("MetricAnomalyTimeline.deviationHigher.label", { pct: abs });
  }
  if (pct < 0) {
    return t("MetricAnomalyTimeline.deviationLower.label", { pct: abs });
  }
  return t("MetricAnomalyTimeline.deviationExact.label");
};

/**
 * @param {number} affected
 * @param {number} total
 * @param {(key: string, opts?: object) => string} t
 */
export const formatPeriodsAffected = (affected, total, t) =>
  t("MetricAnomalyTimeline.periodsAffected.label", { affected, total });

/**
 * @param {AnomalySeverity|null|undefined} severity
 * @param {(key: string) => string} t
 */
export const formatSeverityLabel = (severity, t) => {
  if (!severity) return t("MetricAnomalyTimeline.none.label");
  const map = {
    CRITICAL: "AnomalyDetection.critical.label",
    HIGH:     "AnomalyDetection.high.label",
    MEDIUM:   "AnomalyDetection.medium.label",
    LOW:      "AnomalyDetection.low.label",
  };
  return t(map[severity] || severity);
};

/** @typedef {import('../types/anomalyTimeSeries.types').TimeSeriesDataPoint} TimeSeriesDataPoint */

const hasNumeric = (v) => v != null && !Number.isNaN(v);

/**
 * Value pair line for reported vs expected compare views.
 * @param {TimeSeriesDataPoint} dp
 * @param {(key: string, opts?: object) => string} t
 */
export const formatCompareValuesLine = (dp, t) => {
  const reported = hasNumeric(dp.value) ? formatValue(dp.value) : null;
  const expected = hasNumeric(dp.expectedValue) ? formatValue(dp.expectedValue) : null;

  if (reported && expected) {
    return t("MetricAnomalyTimeline.compareValuesLine.label", { reported, expected });
  }
  if (reported && !expected) {
    return t("MetricAnomalyTimeline.compareExpectedMissing.label", { reported });
  }
  if (!reported && expected) {
    return t("MetricAnomalyTimeline.compareReportedMissing.label", { expected });
  }
  return t("MetricAnomalyTimeline.compareBothMissing.label");
};

/**
 * Deviation line for compare views; clarifies when expected value is absent.
 * @param {TimeSeriesDataPoint} dp
 * @param {(key: string, opts?: object) => string} t
 */
export const formatCompareDeviationLine = (dp, t) => {
  if (!hasNumeric(dp.maxDeviationPct)) return null;

  if (!hasNumeric(dp.expectedValue)) {
    const abs = Math.abs(dp.maxDeviationPct).toFixed(1);
    if (dp.maxDeviationPct > 0) {
      return t("MetricAnomalyTimeline.deviationWithoutExpectedHigher.label", { pct: abs });
    }
    if (dp.maxDeviationPct < 0) {
      return t("MetricAnomalyTimeline.deviationWithoutExpectedLower.label", { pct: abs });
    }
    return t("MetricAnomalyTimeline.deviationWithoutExpectedExact.label");
  }

  return formatDeviation(dp.maxDeviationPct, t);
};

/**
 * Tooltip lines for compare chart hover.
 * @param {TimeSeriesDataPoint} dp
 * @param {(key: string, opts?: object) => string} t
 * @returns {string[]}
 */
export const formatCompareTooltipLines = (dp, t) => {
  const lines = [formatCompareValuesLine(dp, t)];
  const deviation = formatCompareDeviationLine(dp, t);
  if (deviation) lines.push(deviation);
  return lines;
};
